import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionList } from '../../../shared/components/DescriptionList/DescriptionList';
import type { Recommendation } from '../services/Recommendation.entity';
import type { RecommendationsService } from '../services/RecommendationsService';

type Props = {
  service: RecommendationsService;
  entity: Recommendation;
};

type ContextField = {
  field: keyof Recommendation['data'];
  message: string;
  getValue: (id: number | string, service: RecommendationsService) => string | undefined;
};

const contextFields: ContextField[] = [
  {
    field: 'stageIds',
    message: 'alertsRecommendations:contextTitle.stage',
    getValue: (id, service) => service.workClassifierStageTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'wellboreTypeIds',
    message: 'alertsRecommendations:contextTitle.wellboreType',
    getValue: (id, service) =>
      service.wellboreTypes.find((v) => v.id === id)?.data['customName']?.toString() ??
      service.wellboreTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'trajectoryTypeIds',
    message: 'alertsRecommendations:contextTitle.trajectoryType',
    getValue: (id, service) => service.trajectoryTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'sectionTypeIds',
    message: 'alertsRecommendations:contextTitle.section',
    getValue: (id, service) => service.sectionTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'holeTypes',
    message: 'alertsRecommendations:contextTitle.holeType',
    getValue: (id) => String(id),
  },
  {
    field: 'stratUnitTypeIds',
    message: 'alertsRecommendations:contextTitle.stratUnit',
    getValue: (id, service) => service.stratUnitTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'reservoirIds',
    message: 'alertsRecommendations:contextTitle.reservoir',
    getValue: (id, service) => service.reservoirTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'runTypeIds',
    message: 'alertsRecommendations:contextTitle.run',
    getValue: (id, service) => service.runTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
  {
    field: 'operationIds',
    message: 'alertsRecommendations:contextTitle.operation',
    getValue: (id, service) => service.operationTypes.find((v) => v.id === id)?.data['name']?.toString(),
  },
];

export function RecommendationContextDetails({ service, entity }: Props) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return contextFields
      .map((field) => {
        const ids = (entity.data[field.field] as (number | string)[]) ?? [];
        if (!ids.length) return null;
        const value = ids
          .map((id) => field.getValue(id, service))
          .filter(Boolean)
          .join(', ');
        return { term: t(field.message), value };
      })
      .filter((item): item is { term: string; value: string } => item !== null);
  }, [entity, service, t]);

  return <DescriptionList items={items} />;
}
